import { useState, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine, Cell,
} from "recharts";

import {
  InfoIcon, WithTooltip, formatPercent, formatCount, werColor, buildCellTooltip,
} from "./metricInfo";

const COMBO_COLORS = {
  silero_omli: "#f25f3a",
  kidsvad_omli: "#3a8df2",
  silero_sarvam: "#9ccc65",
  kidsvad_sarvam: "#fdd835",
  novad_omli: "#8e44ad",
  novad_sarvam: "#16a085",
};

function colorForCombo(combo, idx = 0) {
  return COMBO_COLORS[combo] || ["#f25f3a", "#3a8df2", "#9ccc65", "#fdd835", "#8e44ad", "#16a085"][idx % 6];
}

function isBaselineCombo(name) {
  return typeof name === "string" && name.startsWith("novad_");
}

function ChartCard({ title, info, children, wide = false, footer, controls }) {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div className={`eval-chart-card ${wide ? "wide" : ""}`}>
      <div className="eval-chart-header">
        <h3>
          <span>{title}</span>
          <InfoIcon text={info} />
        </h3>
        <div className="eval-chart-header-actions">
          {controls}
          {footer != null && (
            <button
              type="button"
              className="eval-raw-toggle"
              onClick={() => setShowRaw((v) => !v)}
            >
              {showRaw ? "Hide raw" : "View raw"}
            </button>
          )}
        </div>
      </div>
      <div className="eval-chart-body">{children}</div>
      {showRaw && (
        <pre className="eval-chart-raw">{JSON.stringify(footer, null, 2)}</pre>
      )}
    </div>
  );
}

function NormalizerToggle({ value, onChange, options, descriptions }) {
  return (
    <div className="eval-norm-toggle">
      {options.map((opt) => (
        <WithTooltip key={opt} text={descriptions?.[opt]}>
          <button
            type="button"
            className={`eval-norm-btn ${value === opt ? "active" : ""}`}
            onClick={() => onChange(opt)}
          >
            {opt}
          </button>
        </WithTooltip>
      ))}
    </div>
  );
}

function HeatmapCell({ value, color, tooltip, formatter, baselineRow, baselineCol }) {
  const display = value === null || value === undefined || Number.isNaN(value)
    ? "—"
    : formatter
      ? formatter(value)
      : formatPercent(value);
  const classes = ["eval-heatmap-cell"];
  if (baselineRow) classes.push("baseline-row");
  if (baselineCol) classes.push("baseline-col");
  return (
    <div className={classes.join(" ")} style={{ background: color }}>
      <span className="eval-heatmap-value">{display}</span>
      {tooltip && <span className="eval-tt-content">{tooltip}</span>}
    </div>
  );
}

function CorpusWerMatrix({ chart }) {
  const rows = chart.rows || [];
  const cols = chart.cols || [];
  const matrix = chart.matrix || [];
  const rowDescs = chart.row_descriptions || [];
  const colDescs = chart.col_descriptions || [];

  return (
    <div className="eval-heatmap-wrapper">
      <div
        className="eval-heatmap-grid"
        style={{ gridTemplateColumns: `auto repeat(${cols.length}, minmax(80px, 1fr))` }}
      >
        <div />
        {cols.map((col, c) => (
          <WithTooltip key={col} text={colDescs[c]} className="eval-heatmap-colhead" as="div">
            {col}
          </WithTooltip>
        ))}
        {rows.map((row, r) => (
          <Row
            key={row}
            row={row}
            rowDesc={rowDescs[r]}
            cells={matrix[r] || []}
            cols={cols}
            colDescs={colDescs}
          />
        ))}
      </div>
      {chart.n_total != null && (
        <div className="eval-chart-caption">n = {formatCount(chart.n_total)} audios</div>
      )}
    </div>
  );
}

function Row({ row, rowDesc, cells, cols, colDescs }) {
  const baselineRow = isBaselineCombo(row);
  return (
    <>
      <WithTooltip text={rowDesc} className={`eval-heatmap-rowhead ${baselineRow ? "baseline-row" : ""}`} as="div">
        {row}
      </WithTooltip>
      {cols.map((col, c) => {
        const v = cells[c];
        const tt = buildCellTooltip({
          rowDesc: rowDesc || row,
          colDesc: colDescs[c] || col,
          label: "Corpus WER",
          value: formatPercent(v, 2),
        });
        return (
          <HeatmapCell
            key={col}
            value={v}
            color={werColor(v)}
            tooltip={tt}
            baselineRow={baselineRow}
            baselineCol={false}
          />
        );
      })}
    </>
  );
}

function GroupedHeatmap({ chart, valueLabel = "WER" }) {
  const seriesKeys = useMemo(() => Object.keys(chart.series || {}), [chart.series]);
  const [norm, setNorm] = useState(seriesKeys[0]);
  const rows = chart.rows || [];
  const cols = chart.cols || [];
  const rowDescs = chart.row_descriptions || [];
  const colDescs = chart.col_descriptions || [];
  const seriesDescs = chart.series_descriptions || {};
  const nRows = chart.n_rows || [];
  const matrix = (chart.series || {})[norm] || [];

  return (
    <div className="eval-heatmap-wrapper">
      <NormalizerToggle
        value={norm}
        onChange={setNorm}
        options={seriesKeys}
        descriptions={seriesDescs}
      />
      <div
        className="eval-heatmap-grid"
        style={{ gridTemplateColumns: `auto repeat(${cols.length}, minmax(70px, 1fr)) auto` }}
      >
        <div />
        {cols.map((col, c) => (
          <WithTooltip key={col} text={colDescs[c]} className={`eval-heatmap-colhead ${isBaselineCombo(col) ? "baseline-col" : ""}`} as="div">
            {col}
          </WithTooltip>
        ))}
        <div className="eval-heatmap-colhead muted">n</div>
        {rows.map((row, r) => {
          const rd = rowDescs[r] && rowDescs[r].length > 0 ? rowDescs[r] : row;
          return (
            <ContextRow
              key={row}
              row={row}
              rowDesc={rd}
              cells={matrix[r] || []}
              cols={cols}
              colDescs={colDescs}
              norm={norm}
              normDesc={seriesDescs[norm]}
              n={nRows[r]}
              valueLabel={valueLabel}
            />
          );
        })}
      </div>
    </div>
  );
}

function ContextRow({ row, rowDesc, cells, cols, colDescs, norm, normDesc, n, valueLabel }) {
  return (
    <>
      <WithTooltip text={rowDesc} className="eval-heatmap-rowhead" as="div">
        {row}
      </WithTooltip>
      {cols.map((col, c) => {
        const v = cells[c];
        const cd = colDescs[c] && colDescs[c].length > 0 ? colDescs[c] : col;
        const tt = buildCellTooltip({
          rowDesc: rowDesc,
          colDesc: `${cd}\n(normalizer: ${normDesc || norm})`,
          label: valueLabel,
          value: formatPercent(v, 2),
        });
        return (
          <HeatmapCell
            key={col}
            value={v}
            color={werColor(v)}
            tooltip={tt}
            baselineCol={isBaselineCombo(col)}
          />
        );
      })}
      <div className="eval-heatmap-n">{formatCount(n)}</div>
    </>
  );
}

function WerBuckets({ chart }) {
  const seriesKeys = useMemo(() => Object.keys(chart.series || {}), [chart.series]);
  const [norm, setNorm] = useState(seriesKeys[0]);
  const buckets = chart.x || [];
  const bucketDescs = chart.x_descriptions || [];
  const comboMap = (chart.series || {})[norm] || {};
  const combos = Object.keys(comboMap);
  const seriesDescs = chart.series_descriptions || {};
  const comboDescs = chart.combo_descriptions || {};

  const data = buckets.map((bucket, i) => {
    const row = { bucket };
    combos.forEach((combo) => {
      row[combo] = comboMap[combo]?.[i] ?? 0;
    });
    return row;
  });

  const tooltipFormatter = (value, name) => {
    const desc = comboDescs[name];
    return [formatCount(value) + (desc ? ` — ${desc.split(".")[0]}.` : ""), name];
  };

  return (
    <div className="eval-wer-buckets">
      <NormalizerToggle
        value={norm}
        onChange={setNorm}
        options={seriesKeys}
        descriptions={seriesDescs}
      />
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={data} margin={{ top: 10, right: 16, bottom: 36, left: 8 }}>
          <CartesianGrid stroke="rgba(127,127,127,0.15)" strokeDasharray="3 3" />
          <XAxis
            dataKey="bucket"
            tick={({ x, y, payload }) => {
              const idx = buckets.indexOf(payload.value);
              const desc = bucketDescs[idx];
              return (
                <g transform={`translate(${x},${y})`}>
                  <text textAnchor="middle" dy={14} fontSize={11} fill="currentColor">
                    {payload.value}
                  </text>
                  {desc && <title>{desc}</title>}
                </g>
              );
            }}
            interval={0}
          />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCount} />
          <Tooltip formatter={tooltipFormatter} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {combos.map((combo, i) => (
            <Bar
              key={combo}
              dataKey={combo}
              stackId="a"
              fill={colorForCombo(combo, i)}
              fillOpacity={isBaselineCombo(combo) ? 0.55 : 1}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function BestComboByAge({ chart }) {
  const seriesKeys = Object.keys(chart.winner || {});
  const rows = chart.rows || [];
  const rowDescs = chart.row_descriptions || [];
  const seriesDescs = chart.series_descriptions || {};
  const nRows = chart.n_rows || [];

  return (
    <div className="eval-best-table-wrapper">
      <table className="eval-best-table">
        <thead>
          <tr>
            <th>Age tier</th>
            {seriesKeys.map((s) => (
              <th key={s}>
                <WithTooltip text={seriesDescs[s]}>{s}</WithTooltip>
              </th>
            ))}
            <th>n</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => {
            const rd = rowDescs[r] && rowDescs[r].length > 0 ? rowDescs[r] : row;
            return (
              <tr key={row}>
                <td>
                  <WithTooltip text={rd}>{row}</WithTooltip>
                </td>
                {seriesKeys.map((s) => {
                  const winner = chart.winner?.[s]?.[r];
                  const wer = chart.wer?.[s]?.[r];
                  return (
                    <td key={s}>
                      <WithTooltip
                        text={buildCellTooltip({
                          rowDesc: rd,
                          colDesc: seriesDescs[s] || s,
                          label: `Best: ${winner ?? "—"}`,
                          value: formatPercent(wer, 2),
                        })}
                      >
                        <span className="eval-best-combo">{winner ?? "—"}</span>
                        <span className="eval-best-wer">{formatPercent(wer, 1)}</span>
                      </WithTooltip>
                    </td>
                  );
                })}
                <td className="muted">{formatCount(nRows[r])}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GroupedBar({ chart, formatter = formatCount, percentY = false, descriptionGetter }) {
  const seriesKeys = Object.keys(chart.series || {});
  const xs = chart.x || [];
  const xDescs = chart.x_descriptions || [];
  const seriesDescs = chart.series_descriptions || {};

  const data = xs.map((x, i) => {
    const row = { x };
    seriesKeys.forEach((s) => {
      const v = chart.series[s];
      if (Array.isArray(v)) row[s] = v[i];
      else if (v && typeof v === "object") row[s] = v[x];
    });
    return row;
  });

  const tooltipFormatter = (value, name) => {
    if (value === null || value === undefined) return ["—", name];
    if (percentY) return [formatPercent(value, 2), name];
    return [formatter(value), name];
  };

  const colors = ["#f25f3a", "#3a8df2", "#9ccc65", "#fdd835"];

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} margin={{ top: 10, right: 16, bottom: 48, left: 8 }}>
        <CartesianGrid stroke="rgba(127,127,127,0.15)" strokeDasharray="3 3" />
        <XAxis
          dataKey="x"
          tick={({ x, y, payload }) => {
            const idx = xs.indexOf(payload.value);
            const desc = descriptionGetter?.(payload.value, idx) || xDescs[idx];
            return (
              <g transform={`translate(${x},${y})`}>
                <text
                  textAnchor="end"
                  transform="rotate(-25)"
                  dy={10}
                  dx={-4}
                  fontSize={11}
                  fill="currentColor"
                >
                  {payload.value}
                </text>
                {desc && <title>{desc}</title>}
              </g>
            );
          }}
          interval={0}
          height={48}
        />
        <YAxis
          tick={{ fontSize: 11 }}
          tickFormatter={percentY ? (v) => `${(v * 100).toFixed(0)}%` : formatCount}
        />
        <Tooltip formatter={tooltipFormatter} />
        <Legend
          wrapperStyle={{ fontSize: 12 }}
          formatter={(name) => {
            const desc = seriesDescs[name];
            return desc ? <span title={desc}>{name}</span> : name;
          }}
        />
        {seriesKeys.map((s, i) => (
          <Bar key={s} dataKey={s} fill={colors[i % colors.length]} name={s} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function DeltaBar({ chart }) {
  const xs = chart.x || [];
  const xDescs = chart.x_descriptions || [];
  const deltas = chart.series?.delta || [];

  const data = xs.map((x, i) => ({ x, delta: deltas[i] }));

  return (
    <>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 10, right: 16, bottom: 48, left: 8 }}>
          <CartesianGrid stroke="rgba(127,127,127,0.15)" strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            tick={({ x, y, payload }) => {
              const idx = xs.indexOf(payload.value);
              return (
                <g transform={`translate(${x},${y})`}>
                  <text textAnchor="end" transform="rotate(-25)" dy={10} dx={-4} fontSize={11} fill="currentColor">
                    {payload.value}
                  </text>
                  {xDescs[idx] && <title>{xDescs[idx]}</title>}
                </g>
              );
            }}
            interval={0}
            height={48}
          />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v * 100).toFixed(1)}%`} />
          <ReferenceLine y={0} stroke="#888" strokeWidth={1} />
          <Tooltip formatter={(v) => [formatPercent(v, 2), "delta"]} />
          <Bar dataKey="delta">
            {data.map((d, i) => (
              <Cell key={i} fill={d.delta >= 0 ? "#3a8df2" : "#e53935"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {chart.interpretation && (
        <div className="eval-chart-caption">{chart.interpretation}</div>
      )}
    </>
  );
}

function GenericDump({ chart }) {
  return (
    <pre className="eval-chart-raw open">{JSON.stringify(chart, null, 2)}</pre>
  );
}

export function WerChartRenderer({ chart }) {
  if (!chart) return null;
  const title = chart.title;
  const info = chart.description;
  const wide = chart._wide;

  let body;
  let bodyKnown = true;

  if (chart.id === "corpus_wer_matrix") {
    body = <CorpusWerMatrix chart={chart} />;
  } else if (
    chart.id === "wer_by_age_combo" ||
    chart.id === "wer_by_audio_label" ||
    chart.id === "wer_by_quality_flag"
  ) {
    body = <GroupedHeatmap chart={chart} valueLabel="WER" />;
  } else if (chart.id === "wer_buckets") {
    body = <WerBuckets chart={chart} />;
  } else if (chart.id === "best_combo_by_age") {
    body = <BestComboByAge chart={chart} />;
  } else if (chart.id === "regressions") {
    body = <GroupedBar chart={chart} formatter={formatCount} percentY={false} />;
  } else if (chart.id === "corpus_llm_wer") {
    body = <GroupedBar chart={chart} percentY={true} />;
  } else if (chart.id === "llm_wer_vs_standard") {
    body = <DeltaBar chart={chart} />;
  } else {
    bodyKnown = false;
    body = <GenericDump chart={chart} />;
  }

  return (
    <ChartCard title={title} info={info} wide={wide} footer={chart}>
      {body}
      {!bodyKnown && (
        <div className="eval-chart-caption">
          Unknown chart id "{chart.id}" — showing raw payload.
        </div>
      )}
    </ChartCard>
  );
}
